"use client";

import React, { useState } from "react";
import { Search, ArrowRight, Instagram, Facebook, Twitter, Linkedin, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const SERVICE_CATEGORIES = [{
  id: "top",
  label: "Full Management",
  color: "bg-primary text-primary-foreground"
}, {
  id: "book",
  label: "Tenant Screening",
  color: "bg-secondary text-secondary-foreground"
}, {
  id: "new",
  label: "Maintenance",
  color: "bg-muted text-muted-foreground"
}, {
  id: "all",
  label: "All Services",
  color: "bg-accent text-accent-foreground"
}];
const SERVICES = [{
  id: 1,
  title: "Property Management",
  description: "Comprehensive oversight of your Orlando investment properties and portfolios.",
  count: "Orlando, FL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1608256/img/orlando_property_management.png"
}, {
  id: 2,
  title: "Tenant Screening",
  description: "Rigorous background checks to ensure high-quality, reliable long-term tenants.",
  count: "Winter Park",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1608256/img/tenant_screening_services.png"
}, {
  id: 3,
  title: "Rent Collection",
  description: "Streamlined digital payment systems to ensure consistent and timely cash flow.",
  count: "Windermere",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1608256/img/rent_collection_orlando.png"
}];
const FOOTER_LINKS = {
  services: [{
    label: "Property Management",
    href: "/RentalAnalysisPage"
  }, {
    label: "Tenant Screening",
    href: "/OwnersPage"
  }, {
    label: "Rent Collection",
    href: "/OwnersPage"
  }, {
    label: "Maintenance",
    href: "/TenantsPage"
  }, {
    label: "Leasing Services",
    href: "/RentalsPage"
  }, {
    label: "Renter's Insurance",
    href: "/RentersInsurancePage"
  }],
  company: [{
    label: "About Us",
    href: "/AboutUsPage"
  }, {
    label: "Contact Us",
    href: "/ContactPage"
  }, {
    label: "Insights",
    href: "/InsightsPage"
  }]
};
function HeroSection() {
  return <div className="relative min-h-[80vh] flex items-center justify-center bg-primary overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1608256/img/orlando_skyline_hero.png" alt="Orlando Skyline" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/70 to-primary/30" />
      </div>

      {}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-24 md:py-32 flex flex-col items-start">
        <Badge variant="outline" className="text-primary-foreground border-accent/50 mb-6 uppercase tracking-widest text-xs bg-primary/20 backdrop-blur-sm">Realsource Property Management - central florida&nbsp; Experts</Badge>
        
        <h1 className="font-default text-5xl md:text-7xl font-bold text-primary-foreground leading-tight mb-6 max-w-4xl"><span className="italic font-light text-accent">Contact Us</span>&nbsp;in Orlando</h1>
        
        
        
        <div className="flex flex-wrap gap-4 mb-16">
          <Button size="lg" asChild className="rounded-md bg-accent text-accent-foreground hover:bg-accent/90 px-8 shadow-lg">
            
          </Button>
          <Button size="lg" variant="outline" asChild className="rounded-md bg-background/10 text-primary-foreground hover:bg-background/20 px-8 border-primary-foreground/30 backdrop-blur-sm">
            
          </Button>
        </div>

        {}
        <div className="flex items-center gap-4">
          
          
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,121.32,201.2,112.5,242.49,107.36,282.8,92.2,321.39,56.44Z" className="fill-background"></path>
        </svg>
      </div>
    </div>;
}
function ServicesSection() {
  return;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-[400px] rounded-lg bg-muted animate-pulse" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground font-default">
        No featured listings available at this moment.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="overflow-hidden border-border shadow-md rounded-lg group hover:shadow-xl transition-all flex flex-col">
          <Link to={product.link} className="relative h-64 bg-muted overflow-hidden block shrink-0">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/10 text-secondary">
                Property Image
              </div>}
            <Badge className="absolute top-4 left-4 bg-accent text-accent-foreground rounded-md px-3 py-1">
              New Listing
            </Badge>
          </Link>
          <CardContent className="p-6 flex flex-col flex-grow">
            <Link to={product.link}>
              <h3 className="font-default text-xl font-semibold text-primary mb-2 hover:text-secondary transition-colors line-clamp-1">
                {product.title}
              </h3>
            </Link>
            {product.short_description && <div className="text-muted-foreground text-sm mb-4 font-default line-clamp-2" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
            <div className="mt-auto pt-4 flex items-center justify-between">
              <span className="font-default font-bold text-lg text-primary">
                ${product.price?.toLocaleString() || '0'}/mo
              </span>
              <div className="w-32">
                <Button asChild className="w-full rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/90">
                  <Link to={product.link}>View Details</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
function ProductsSection() {
  const wp_query = new WP_Query({
    per_page: 3
  });
  return;
}
function CTASection() {
  return;
}
function FooterSection() {
  return;
}
export default function LandingPage() {
  return <section data-section-id="224119" id="contacthero" className="bg-background text-foreground w-full overflow-x-hidden">
      <HeroSection />
      <ServicesSection />
      <ProductsSection />
      <CTASection />
      <FooterSection />
    </section>;
}